import AnnualOutlook from './AnnualOutlook';
import DashboardFilters from './DashboardFilters';
import { PageNotice } from '../../../shared/components/PageFeedback';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Euro,
  TrendingDown,
  Wallet,
  TrendingUp,
  BarChart3,
} from '../../../shared/assets/AppIcons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { getRoutePath, ROUTE_IDS } from '@/app/router/route-registry';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from 'recharts';

import {
  formatCurrency,
  getBalanceTone,
  getChartColor,
  formatChartAmount,
  toLocalDateKey,
} from '@/modules/dashboard/utils/dashboard.helpers';
import DashboardCustomTooltip from '@/modules/dashboard/components/DashboardCustomTooltip';
import DashboardLineChartTooltip from '@/modules/dashboard/components/DashboardLineChartTooltip';
import '@/modules/dashboard/styles/AccountingSection.css';

export default function AccountingSection({
  accounting,
  unavailable = false,
  lineChartData,
  bilancioData,
  incassiClientiData,
  speseTipoData,
  dateRange,
  setDateRange,
  isCalendarOpen,
  setIsCalendarOpen,
  applyDatePreset,
  dateRangeLabel,
}) {
  const [filterToolbarContainer, setFilterToolbarContainer] = useState(null);
  const accountingPath = getRoutePath(ROUTE_IDS.ACCOUNTING);
  const expensesPath = getRoutePath(ROUTE_IDS.EXPENSES);
  const chartColors = {
    grid: 'var(--chart-available)',
    axis: 'var(--text-secondary)',
    revenue: 'var(--chart-success)',
    costs: 'var(--chart-danger)',
    profit: 'var(--primary)',
  };

  const rangeParams = new URLSearchParams({
    periodFrom: toLocalDateKey(dateRange?.from),
    periodTo: toLocalDateKey(dateRange?.to),
  });
  const accountingTarget = `${accountingPath}?${rangeParams}`;

  if (unavailable) return <Card className="accounting-section">
    <CardHeader><CardTitle>Bilancio</CardTitle></CardHeader>
    <CardContent><PageNotice role="alert" className="wt-inline-error">Dati contabili non disponibili. Impossibile calcolare importi e grafici.</PageNotice></CardContent>
  </Card>;

  return (
    <Card className="accounting-section">
      <CardHeader>
        <div className="accounting-section__header">
          <div className="accounting-section__title-wrap">
            <CardTitle className="accounting-section__title">
              <BarChart3 className="accounting-section__title-icon" size={16} />
              Bilancio Aziendale
            </CardTitle>

            <CardDescription className="accounting-section__description">
              Movimenti registrati e scadenze aziendali
            </CardDescription>
          </div>

          <div className="accounting-section__actions">

            <Link to={accountingPath}>
              <Badge variant="outline" className="accounting-section__link-badge">
                Incassi
              </Badge>
            </Link>

            <Link to={expensesPath}>
              <Badge variant="outline" className="accounting-section__link-badge">
                Uscite
              </Badge>
            </Link>
            <div ref={setFilterToolbarContainer} className="dashboard-section-filter-actions" />
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="dashboard-section-filters">
          <span className="dashboard-section-filters__period">{dateRangeLabel}</span>
          <DashboardFilters toolbarContainer={filterToolbarContainer} label="Filtri bilancio" accounting={{ range: dateRange, setRange: setDateRange, applyPreset: applyDatePreset }} />
        </div>
        <div className="accounting-section__metrics-grid">
          <Link to={accountingTarget} className="accounting-section__metric-card accounting-section__metric-card--success" aria-label="Apri gli incassi del periodo in Contabilità">
            <div className="accounting-section__metric-header">
              <span className="accounting-section__metric-label accounting-section__metric-label--success">
                Incassi
              </span>
              <Euro
                size={16}
                className="accounting-section__metric-icon accounting-section__metric-icon--success"
              />
            </div>

            <p className="accounting-section__metric-value accounting-section__metric-value--success">
              {formatCurrency(accounting.incassati)}
            </p>

            <p className="accounting-section__metric-subtext accounting-section__metric-subtext--success">
              su {formatCurrency(accounting.totalIncassi)} previsti
            </p>
          </Link>

          <Link to={`${expensesPath}?periodFrom=${rangeParams.get('periodFrom')}&periodTo=${rangeParams.get('periodTo')}`} className="accounting-section__metric-card accounting-section__metric-card--attention" aria-label="Apri le uscite del periodo">
            <div className="accounting-section__metric-header">
              <span className="accounting-section__metric-label accounting-section__metric-label--neutral">
                Uscite
              </span>
              <TrendingDown
                size={16}
                className="accounting-section__metric-icon accounting-section__metric-icon--neutral"
              />
            </div>

            <p className="accounting-section__metric-value accounting-section__metric-value--neutral">
              {formatCurrency(accounting.uscitePagate)}
            </p>

            <p className="accounting-section__metric-subtext accounting-section__metric-subtext--neutral">
              su {formatCurrency(accounting.totalUscite)} previste
            </p>
          </Link>

          <Link to={accountingTarget} className={`accounting-section__metric-card accounting-section__metric-card--${getBalanceTone(accounting.utilePeriodo)}`} aria-label="Apri Contabilità per l'utile del periodo">
            <div className="accounting-section__metric-header">
              <span className="accounting-section__metric-label accounting-section__metric-label--neutral">
                Utile
              </span>
              <Wallet
                size={16}
                className="accounting-section__metric-icon accounting-section__metric-icon--neutral"
              />
            </div>

            <p className="accounting-section__metric-value">
              {formatCurrency(accounting.utilePeriodo)}
            </p>

            <p className="accounting-section__metric-subtext accounting-section__metric-subtext--neutral">
              Incassi - Uscite
            </p>
          </Link>

          <Link to={`${accountingTarget}&metric=monthly-profit`} className={`accounting-section__metric-card accounting-section__metric-card--${getBalanceTone(accounting.guadagnoMensile)}`} aria-label="Apri Contabilità per il guadagno mensile">
            <div className="accounting-section__metric-header">
              <span className="accounting-section__metric-label accounting-section__metric-label--accent">
                Guadagno mensile
              </span>
              <TrendingUp
                size={16}
                className="accounting-section__metric-icon accounting-section__metric-icon--accent"
              />
            </div>

            <p className="accounting-section__metric-value">
              {formatCurrency(accounting.guadagnoMensile)}
            </p>

            <p className="accounting-section__metric-subtext accounting-section__metric-subtext--accent">
              Incassi - Uscite
            </p>
          </Link>
        </div>

        <div className="accounting-section__charts-grid">
          <div className="accounting-section__chart-box">
            <h4 className="accounting-section__chart-title">Andamento Aziendale</h4>
            <p className="accounting-section__chart-caption">Incassi e uscite registrati · {dateRangeLabel}</p>

            {lineChartData.some((point) => point.fatturato || point.costi) ? <ResponsiveContainer width="100%" minWidth={0} height={250}>
              <LineChart data={lineChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis dataKey="mese" tick={{ fontSize: 12 }} stroke={chartColors.axis} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke={chartColors.axis}
                  tickFormatter={formatChartAmount}
                  width={65}
                />
                <Tooltip content={<DashboardLineChartTooltip />} />
                <Legend />
                <Line
                  type="linear"
                  isAnimationActive={false}
                  dataKey="fatturato"
                  stroke={chartColors.revenue}
                  strokeWidth={2}
                  name="Incassi"
                  dot={{ r: 3 }}
                />
                <Line
                  type="linear"
                  isAnimationActive={false}
                  dataKey="costi"
                  stroke={chartColors.costs}
                  strokeWidth={2}
                  name="Uscite"
                  dot={{ r: 3 }}
                />
                <Line
                  type="linear"
                  isAnimationActive={false}
                  dataKey="utile"
                  stroke={chartColors.profit}
                  strokeWidth={2}
                  name="Utile"
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer> : <p role="status" className="accounting-section__chart-empty">Nessun movimento registrato nel periodo.</p>}
          </div>

          <div className="accounting-section__chart-box">
            <h4 className="accounting-section__chart-title">Distribuzione</h4>

            <div className="accounting-section__pie-grid">
              <div className="accounting-section__pie-item">
                <p className="accounting-section__pie-label">Bilancio</p>
                {bilancioData.some((entry) => entry.value > 0) ? <ResponsiveContainer width="100%" minWidth={0} height={120}>
                  <RechartsPieChart>
                    <Pie
                      isAnimationActive={false}
                      data={bilancioData}
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={45}
                      dataKey="value"
                    >
                      {bilancioData.map((entry, index) => (
                        <Cell key={`bilancio-${index}`} fill={getChartColor(entry.colorKey)} />
                      ))}
                    </Pie>
                    <Tooltip content={<DashboardCustomTooltip />} />
                  </RechartsPieChart>
                </ResponsiveContainer> : <p role="status" className="accounting-section__pie-empty">Nessun dato nel periodo</p>}
              </div>

              <div className="accounting-section__pie-item">
                <p className="accounting-section__pie-label">Incassi</p>
                {incassiClientiData.some((entry) => entry.value > 0) ? <ResponsiveContainer width="100%" minWidth={0} height={120}>
                  <RechartsPieChart>
                    <Pie
                      isAnimationActive={false}
                      data={incassiClientiData}
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={45}
                      dataKey="value"
                    >
                      {incassiClientiData.map((entry, index) => (
                        <Cell key={`incassi-${index}`} fill={getChartColor(entry.colorKey)} />
                      ))}
                    </Pie>
                    <Tooltip content={<DashboardCustomTooltip />} />
                  </RechartsPieChart>
                </ResponsiveContainer> : <p role="status" className="accounting-section__pie-empty">Nessun dato nel periodo</p>}
              </div>

              <div className="accounting-section__pie-item">
                <p className="accounting-section__pie-label">Spese</p>
                {speseTipoData.some((entry) => entry.value > 0) ? <ResponsiveContainer width="100%" minWidth={0} height={120}>
                  <RechartsPieChart>
                    <Pie
                      isAnimationActive={false}
                      data={speseTipoData}
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={45}
                      dataKey="value"
                    >
                      {speseTipoData.map((entry, index) => (
                        <Cell key={`spese-${index}`} fill={getChartColor(entry.colorKey)} />
                      ))}
                    </Pie>
                    <Tooltip content={<DashboardCustomTooltip />} />
                  </RechartsPieChart>
                </ResponsiveContainer> : <p role="status" className="accounting-section__pie-empty">Nessun dato nel periodo</p>}
              </div>
            </div>
          </div>
        </div>

        <AnnualOutlook outlook={accounting.proiezioneAnnua} />
      </CardContent>
    </Card>
  );
}
